import type { BulkSessionDataOutput } from '@sunrise-ui/api/events';

export enum SessionRole {
  MODERATOR = 'moderator',
  SPEAKER = 'speaker',
}

/**
 * Simple deterministic shuffle using a seeded pseudo-random number generator
 * Based on the user ID to ensure consistent ordering per user
 */
const deterministicShuffle = <T>(array: T[], seed: string): T[] => {
  if (!array.length) return array;

  // Create a simple hash from the seed string
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use the hash as the initial seed for our PRNG
  let seedValue = Math.abs(hash);

  // Simple linear congruential generator for deterministic randomness
  const random = () => {
    seedValue = (seedValue * 1664525 + 1013904223) % 4294967296;
    return seedValue / 4294967296;
  };

  // Fisher-Yates shuffle with our deterministic random function
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

/**
 * Helper function to sort sessions, prioritizing sessions where the current user is a speaker or moderator
 * @param sessions - Array of sessions to sort
 * @param currentUserId - ID of the current user
 * @param bulkSessionData - Bulk session data containing attendee information
 * @returns Sorted array with user's sessions (as speaker/moderator) at the top
 */
export const sortSessionsByUserRole = <T extends { id: string }>(
  sessions: T[] | undefined,
  currentUserId: string | undefined,
  bulkSessionData:
    | {
        session_data?: Array<{
          session_id: string;
          data: {
            attendees?: Array<{
              user_id: string;
              role: string;
            }>;
          };
        }>;
      }
    | undefined,
): T[] | undefined => {
  if (!sessions) {
    return sessions;
  }

  // Handle logged-out users: shuffle randomly each time using timestamp
  if (!currentUserId) {
    const timestamp = Date.now().toString();
    return deterministicShuffle(sessions, timestamp);
  }

  // Handle logged-in users without bulk data: use deterministic shuffle based on user ID
  if (!bulkSessionData?.session_data) {
    return deterministicShuffle(sessions, currentUserId);
  }

  const isUserSpeakerOrModerator = (sessionId: string): boolean => {
    const sessionData = bulkSessionData.session_data?.find(
      (data) => data.session_id === sessionId,
    );

    if (!sessionData?.data.attendees) {
      return false;
    }

    return sessionData.data.attendees.some(
      (attendee) =>
        attendee.user_id === currentUserId &&
        (attendee.role === SessionRole.SPEAKER ||
          attendee.role === SessionRole.MODERATOR),
    );
  };

  // Separate user sessions from other sessions
  const userSessions: T[] = [];
  const otherSessions: T[] = [];

  sessions.forEach((session) => {
    if (isUserSpeakerOrModerator(session.id)) {
      userSessions.push(session);
    } else {
      otherSessions.push(session);
    }
  });

  // Shuffle other sessions deterministically based on user ID
  const shuffledOtherSessions = deterministicShuffle(
    otherSessions,
    currentUserId,
  );

  // Return user sessions first, then shuffled other sessions
  return [...userSessions, ...shuffledOtherSessions];
};

/**
 * Lightweight types for the subset of bulk session data used by CSV generation
 */
export type BulkSessionData = {
  session_data?: Array<{
    session_id: string;
    data?: {
      session_tags?: Array<{ tag_id: string }>;
      session_categories?: Array<{ category_id: string }>;
      attendees?: Array<{ user_id?: string; role?: string }>;
    };
  }>;
};

export type SessionVoteCountItem = {
  session_id: string;
  vote_count: number;
};

export type SessionForCSV = {
  id: string;
  topic_name?: string | null;
  topic_description?: string | null;
};

/**
 * Escape CSV values that contain commas, quotes, or newlines
 */
export const escapeCSVValue = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/**
 * Generate a CSV (session-oriented) with columns:
 * Session Title, Session Description, Industries, Track, Number of Speakers, Number of Votes
 *
 * This is a pure utility (but will be moved to the backend in future). Resolve tag/category names via maps if available.
 */
/**
 * Types for attendee CSV generation using ticket receipt data
 */
export type TicketReceiptForCSV = {
  first_name: string;
  last_name: string;
  email: string;
  ticket_type_id: string;
  price: number | string;
  promo_code?: string;
};

export type TicketTypeForCSV = {
  id: string;
  name?: string;
};

/**
 * Generate a CSV of attendees with columns:
 * First Name, Last Name, Email, Ticket Type, Amount Paid, Discount Code
 */
export const generateAttendeeCSV = ({
  ticketReceipts,
  ticketTypes,
}: {
  ticketReceipts: TicketReceiptForCSV[] | undefined;
  ticketTypes?: TicketTypeForCSV[];
}): string | undefined => {
  if (!ticketReceipts || ticketReceipts.length === 0) {
    return undefined;
  }

  const csvRows: string[] = [];
  csvRows.push(
    'First Name,Last Name,Email,Ticket Type,Amount Paid,Discount Code',
  );

  // Create ticket type name lookup
  const ticketTypeNameById: Record<string, string> = {};
  if (ticketTypes) {
    ticketTypes.forEach(({ id, name }) => {
      if (id && name) {
        ticketTypeNameById[id] = name;
      }
    });
  }

  ticketReceipts.forEach((receipt) => {
    const { first_name, last_name, email, ticket_type_id, price, promo_code } =
      receipt;

    // Get ticket type name or fallback to ID
    const ticketTypeName = ticketTypeNameById[ticket_type_id] || ticket_type_id;

    // Format price as currency (assumes USD)
    const numericPrice =
      typeof price === 'number' ? price : parseFloat(price) || 0;
    const formattedPrice = `$${numericPrice.toFixed(2)}`;

    // Add row
    csvRows.push(
      [
        escapeCSVValue(first_name || ''),
        escapeCSVValue(last_name || ''),
        escapeCSVValue(email || ''),
        escapeCSVValue(ticketTypeName),
        escapeCSVValue(formattedPrice),
        escapeCSVValue(promo_code || ''),
      ].join(','),
    );
  });

  return csvRows.join('\n');
};

export const generateSessionCSV = ({
  sessions,
  bulkSessionData,
  voteCounts,
  tagNameById,
  categoryNameById,
}: {
  sessions: SessionForCSV[] | undefined;
  bulkSessionData: BulkSessionData | undefined;
  voteCounts?: SessionVoteCountItem[] | undefined;
  tagNameById?: Record<string, string>;
  categoryNameById?: Record<string, string>;
}): string | undefined => {
  if (!sessions || !bulkSessionData?.session_data) {
    return undefined;
  }

  const csvRows: string[] = [];
  csvRows.push(
    'Session Title,Session Description,Industries,Track,Number of Speakers,Number of Votes',
  );

  const tagMap = tagNameById || {};
  const categoryMap = categoryNameById || {};
  const votes = voteCounts || [];

  sessions.forEach((session) => {
    const { id: sessionId, topic_name, topic_description } = session;

    const sessionData = bulkSessionData.session_data?.find(
      (data) => data.session_id === sessionId,
    );

    // Count speakers and moderators
    let speakerCount = 0;
    if (sessionData?.data?.attendees) {
      speakerCount = sessionData.data.attendees.filter(
        (attendee) =>
          attendee?.role === SessionRole.SPEAKER ||
          attendee?.role === SessionRole.MODERATOR,
      ).length;
    }

    // Tags (industries) and categories
    const sessionTags = sessionData?.data?.session_tags || [];
    const sessionCategories = sessionData?.data?.session_categories || [];

    const tagNames = sessionTags
      .map(({ tag_id }) => tagMap[tag_id])
      .filter((name): name is string => Boolean(name));

    const trackName =
      sessionCategories.length > 0
        ? categoryMap[sessionCategories[0].category_id] || ''
        : '';

    // Vote count
    const voteCount =
      votes.find((v) => v.session_id === sessionId)?.vote_count ?? 0;

    // Add row
    csvRows.push(
      [
        escapeCSVValue(topic_name || ''),
        escapeCSVValue(topic_description || ''),
        escapeCSVValue(tagNames.join(', ')),
        escapeCSVValue(trackName),
        speakerCount.toString(),
        voteCount.toString(),
      ].join(','),
    );
  });

  return csvRows.join('\n');
};

/**
 * Filter community sessions by tags and categories
 * @param sessions - Array of sessions to filter
 * @param filterTagIds - Array of tag IDs to filter by
 * @param filterCategoryIds - Array of category IDs to filter by
 * @param bulkSessionData - Bulk session data containing tags and categories
 * @returns Filtered array of sessions
 */
export const filterCommunitySessions = <T extends { id: string }>(
  sessions: T[] | undefined,
  filterTagIds: string[],
  filterCategoryIds: string[],
  bulkSessionData: BulkSessionDataOutput | undefined,
): T[] | undefined => {
  if (!sessions || sessions.length === 0) {
    return sessions;
  }

  // If no filters applied, return all sessions
  if (filterTagIds.length === 0 && filterCategoryIds.length === 0) {
    return sessions;
  }

  if (!bulkSessionData?.session_data) {
    return sessions; // Return unfiltered sessions if no bulk data available
  }

  // Filter sessions based on tags and categories
  return sessions.filter((session) => {
    const sessionData = bulkSessionData.session_data?.find(
      (data) => data.session_id === session.id,
    );

    if (!sessionData?.data) {
      return true; // Include session if no data available
    }

    // Get session's tag and category IDs
    const sessionTagIds =
      sessionData.data.session_tags?.map((tag) => tag.tag_id) || [];
    const sessionCategoryIds =
      sessionData.data.session_categories?.map((cat) => cat.category_id) || [];

    let matchesFilter = true;

    if (filterTagIds.length > 0) {
      matchesFilter =
        matchesFilter &&
        filterTagIds.some((tagId) => sessionTagIds.includes(tagId));
    }

    if (filterCategoryIds.length > 0) {
      matchesFilter =
        matchesFilter &&
        filterCategoryIds.some((categoryId) =>
          sessionCategoryIds.includes(categoryId),
        );
    }

    return matchesFilter;
  });
};

/**
 * Extract speakers and moderators from community sessions
 * @param sessions - Array of sessions
 * @param bulkSessionData - Bulk session data containing attendees
 * @param profilesCache - Map of user IDs to their profiles
 * @returns Array of speaker/moderator options with value and label
 */
export const getSpeakerModeratorsFromSessions = (
  sessions: Array<{ id: string }> | undefined,
  bulkSessionData: BulkSessionDataOutput | undefined,
  profilesCache: Map<string, { name?: string; email?: string }>,
): Array<{ value: string; label: string }> => {
  if (!sessions || sessions.length === 0 || !bulkSessionData?.session_data) {
    return [];
  }

  // Extract unique speakers and moderators
  const speakerUserIds = new Set<string>();

  bulkSessionData.session_data.forEach((sessionData) => {
    if (sessionData.data?.attendees) {
      sessionData.data.attendees.forEach((attendee) => {
        if (
          attendee.user_id &&
          (attendee.role === SessionRole.SPEAKER ||
            attendee.role === SessionRole.MODERATOR)
        ) {
          speakerUserIds.add(attendee.user_id);
        }
      });
    }
  });

  // Get user profiles for speakers
  const speakerModeratorOptions: Array<{ value: string; label: string }> = [];

  speakerUserIds.forEach((userId) => {
    const profile = profilesCache.get(userId);
    if (profile) {
      speakerModeratorOptions.push({
        value: userId,
        label: profile.name || profile.email || `User ${userId}`,
      });
    }
  });

  // Sort by name
  speakerModeratorOptions.sort((a, b) => a.label.localeCompare(b.label));

  return speakerModeratorOptions;
};
